/**
 * Discipline helper utilities.
 * All functions use safe fallbacks so existing data without discipline fields works unchanged.
 */
import { DISCIPLINES, DEFAULT_DISCIPLINES, getDifficultyConfig } from "@sendly/shared";
import { withDerivedInk, getDifficulty, inkOnFill } from "../utils";

/** Returns the gym's discipline list, fallback: ["boulder"] */
export const getGymDisciplines = (gymMeta) =>
  gymMeta?.disciplines?.length ? gymMeta.disciplines : DEFAULT_DISCIPLINES;

/** True if the gym supports more than one discipline */
export const isMixedGym = (gymMeta) =>
  getGymDisciplines(gymMeta).length > 1;

/** Route discipline, fallback: "boulder" */
export const getRouteDiscipline = (route) =>
  route?.discipline || DISCIPLINES.BOULDER;

/** Competition discipline, fallback: "boulder" */
export const getCompDiscipline = (comp) =>
  comp?.discipline || DISCIPLINES.BOULDER;

/** Filter routes by discipline */
export const filterRoutesByDiscipline = (routes, discipline) =>
  routes.filter((r) => getRouteDiscipline(r) === discipline);

/**
 * Returns the difficulty set for a given discipline, or `null` when the gym has
 * no config at all (every caller spells its own `|| DIFFICULTY`).
 * Mixed gyms: uses difficultyByDiscipline[discipline].
 * Single-discipline or legacy gyms: falls back to customDifficulty.
 *
 * The branch selection is the shared `getDifficultyConfig` — the very function
 * the server prices an ascent with — so the app and `gymStats` cannot disagree
 * on which set a route belongs to. What this wrapper adds is client-only: `tc`
 * is re-derived rather than trusted (same rule as `getDifficulty`), because the
 * resolver returns the RAW stored map, and without it a mixed-discipline gym
 * kept the stale ink that the single-discipline path already drops.
 */
export const getDifficultyForDiscipline = (settings, discipline, gymDisciplines) =>
  withDerivedInk(getDifficultyConfig({ settings, disciplines: gymDisciplines }, discipline)) || null;

/**
 * Seed the per-discipline difficulty sets at the moment a gym becomes mixed.
 *
 * The keys of the two sets MUST be disjoint: `gymStats.diffBreakdown` counts
 * sends per difficulty key with no discipline attached, so the server-side
 * recompute (functions/shared/difficultyPointsMap.js) and every cross-discipline
 * reader (`getAllDifficulties` below) union the sets by key. A lead set cloned
 * with the boulder keys (`blue`, `red`, …) would make "blue" mean two point
 * values at once — the exact ambiguity the old lazy clone in the level editor
 * created.
 *
 * So the incumbent discipline keeps its existing keys (the routes on the wall
 * reference them), and the new discipline gets the same ladder under
 * `<discipline>_`-prefixed keys (`lead_blue`, `lead_red`, …) — the same idea
 * as the outdoor seeder's `b6Ap` / `g8p` prefixes. Both sets are returned so
 * the caller writes them in the SAME save as the `disciplines` flip.
 *
 * A branch that already exists is never touched (a discipline switched off and
 * back on keeps the keys its archived routes still reference).
 *
 * @param {Object} settings          `gym.settings`
 * @param {string[]} currentDisciplines  the disciplines BEFORE the toggle
 * @param {string} newDiscipline     the discipline being switched on
 * @param {Object} defaults          `DIFFICULTY` — used when the gym never customised
 * @param {(key: string, level: Object) => string} [labelFor]  label for a copied
 *   level; lets the caller translate the seeded defaults (`tDiffLabel`) so a
 *   prefixed key — which the i18n table cannot resolve — carries a readable label
 * @returns {Object|null} the full `difficultyByDiscipline` map to write, or null
 *   when every branch already exists and there is nothing to write
 */
export function seedDisciplineDifficulty(settings, currentDisciplines, newDiscipline, defaults, labelFor) {
  const byDisc = { ...(settings?.difficultyByDiscipline || {}) };
  const incumbents = (currentDisciplines || []).filter((d) => d !== newDiscipline);
  const base = byDisc[incumbents[0]] || settings?.customDifficulty || defaults || {};
  let changed = false;
  for (const d of incumbents) {
    if (byDisc[d]) continue;
    byDisc[d] = { ...base };
    changed = true;
  }
  if (!byDisc[newDiscipline]) {
    const prefixed = {};
    for (const [key, level] of Object.entries(base)) {
      prefixed[`${newDiscipline}_${key}`] = {
        ...level,
        label: labelFor ? labelFor(key, level) : level.label,
      };
    }
    byDisc[newDiscipline] = prefixed;
    changed = true;
  }
  return changed ? byDisc : null;
}

/**
 * Every difficulty level the gym has ever keyed a route by, in ONE map — for
 * readers that resolve a `diffBreakdown` (which carries no discipline) or price
 * ascents of mixed disciplines in a single pass: the global profile, the
 * platform user modal, the in-gym user stats modal, the header score.
 *
 * Order of precedence mirrors the live resolvers, so a key never resolves to a
 * different level here than on the map: the gym's active disciplines first
 * (`getDifficultyForDiscipline` view — the branch, or `customDifficulty` for a
 * missing branch), then any stored branch of a discipline that has since been
 * switched off, then the legacy flat `customDifficulty`. Within that order the
 * first definition of a key wins; later sets only add keys. Each set's `order`
 * continues after the previous one, so `sortedEntries` lists boulder levels
 * before lead levels instead of interleaving them.
 *
 * A single-discipline gym without per-discipline branches returns exactly what
 * `getDifficulty` does — including the shared defaults object for an
 * uncustomised gym, because its identity is a memo dependency downstream.
 */
export function getAllDifficulties(settings, gymDisciplines, defaults) {
  const s = settings || {};
  const byDisc = s.difficultyByDiscipline || {};
  const disciplines = gymDisciplines?.length ? gymDisciplines : DEFAULT_DISCIPLINES;
  if (Object.keys(byDisc).length === 0) return getDifficulty(s, defaults);

  const sets = [];
  const push = (cfg) => { if (cfg && !sets.includes(cfg)) sets.push(cfg); };
  if (disciplines.length > 1) {
    for (const d of disciplines) push(byDisc[d] || s.customDifficulty || defaults);
  } else {
    push(s.customDifficulty || defaults);
  }
  for (const cfg of Object.values(byDisc)) push(cfg);
  push(s.customDifficulty);

  const out = {};
  let offset = 0;
  for (const cfg of sets) {
    let maxOrder = -1;
    let i = 0;
    for (const [k, v] of Object.entries(cfg)) {
      const order = v?.order ?? defaults?.[k]?.order ?? i;
      i++;
      if (order > maxOrder) maxOrder = order;
      if (k in out) continue;
      // `tc` is derived, never trusted from storage — same rule as `getDifficulty`.
      out[k] = { ...v, tc: inkOnFill(v?.color), order: offset + order };
    }
    offset += maxOrder + 1;
  }
  return out;
}
